import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import API from '../api/Api';
import { LocalStorage } from '../components';
import initialState from "../constants/initialState";
import storageType from '../constants/storageType';

// Get menu list by venue
export const getMenuByVenue = createAsyncThunk(
    'venueMenu/menuByVenue',
    async (venueId, thunkAPI) => {
        try {
            const result = await API.getData(`venue-menu/venue/${venueId}`);
            const response = result.data;
            if (response.status) {
                return response;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);
// Update menu status
export const updateMenu = createAsyncThunk(
    'venueMenu/menuUpdate',
    async (formData, thunkAPI) => {
        try {
            let params = { ...formData }; // Create copy of state object
            delete params['id']; // Remove id key from object
            const result = await API.putData(`venue-menu/update/${formData.id}`, params);
            const response = result.data;
            if (response.status) {
                toast.success(response.message);
                return response;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);
// Get categories by venue
export const getCategoryByVenue = createAsyncThunk(
    'venueMenu/categoryByVenue',
    async (venueId, thunkAPI) => {
        try {
            const menuId = LocalStorage.getValue(storageType.ACTIVE_MENU_ID);
            const result = await API.getData(`venue-category/venue?venue_id=${venueId}&menu_id=${menuId}&include_hidden=1`);
            const response = result.data;
            if (response.status) {
                return response.data;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

// Create a new venue category
export const createVenueCategory = createAsyncThunk(
    'venueMenu/saveMenuCategory',
    async (formData, thunkAPI) => {
        try {
            let params = {};
            Object.keys(formData).forEach(key => {
                if (key === 'title') {
                    let lang = formData[key];
                    Object.keys(lang).forEach(lKey => {
                        params[lKey] = lang[lKey];
                    });
                } else {
                    params[key] = formData[key];
                }
            });
            const result = await API.postWithImage('venue-category/create', params);
            const response = result.data;
            if (response.status) {
                response['action_status'] = (parseInt(params.id) > 0) ? 'update' : 'insert';
                return response;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);

// Save venue menu sub-categories
export const saveNewSubcategory = createAsyncThunk(
    'venueMenu/saveMenuSubcategory',
    async (formData, thunkAPI) => {
        try {
            let params = {};
            Object.keys(formData).forEach(key => {
                if (key === 'title') {
                    let lang = formData[key];
                    Object.keys(lang).forEach(lKey => {
                        params[lKey] = lang[lKey];
                    });
                } else if (key === 'desc') {
                    // Multilingual description → flat desc_<code> fields the API rebuilds into a map.
                    const lang = formData[key];
                    Object.keys(lang).forEach(lKey => {
                        const code = lKey.replace('lang_', '');
                        if (lang[lKey]) params[`desc_${code}`] = lang[lKey];
                    });
                } else {
                    params[key] = formData[key];
                }
            });
            const result = await API.postWithImage('venue-subcategory/create', params);
            const response = result.data;
            if (response.status) {
                response['action_status'] = (parseInt(params.id) > 0) ? 'update' : 'insert';
                return response;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);
export const updateSubcategory = createAsyncThunk(
    'venueMenu/updateSubcategory',
    async (formData, thunkAPI) => {
        try {
            let params = {};
            Object.keys(formData.data).forEach(key => {
                if (key === 'is_active') {
                    params[key] = formData.active;
                } else {
                    params[key] = formData.data[key];
                }
            });
            params['user_id'] = formData.user_id;
            const result = await API.putData(`venue-subcategory/update/${params.id}`, params);
            const response = result.data;
            if (response.status) {
                return response;
            } else {
                return thunkAPI.rejectWithValue({ error: response.message });
            }
        } catch (err) {
            return thunkAPI.rejectWithValue({ error: err.message });
        }
    }
);
// Persist dish order within a category (drag-and-drop) → sort_order = position, then refresh so
// Redux state matches the saved order. `list` is the reordered dish array from ReactSortable.
export const reOrderSubcategories = (list) => async (dispatch) => {
    const arr = Array.isArray(list) ? list : [];
    if (!arr.length) return;
    const formData = arr.map((item, index) => ({ id: item.id, order_id: index + 1 }));
    await API.postData('venue-subcategory/update-order', formData);
    const venueId = arr[0]?.venue_id;
    if (venueId) return dispatch(getCategoryByVenue(venueId));
};

// Persist category order (drag-and-drop) → sort_order = position, then refresh.
export const reorderCategories = (list) => async (dispatch) => {
    const arr = Array.isArray(list) ? list : [];
    if (!arr.length) return;
    const formData = arr.map((item, index) => ({ id: item.id, sort_order: index + 1 }));
    await API.putData('venue-category/reorder', formData);
    const venueId = arr[0]?.venue_id;
    if (venueId) return dispatch(getCategoryByVenue(venueId));
};

//Define initial state
const venueMenuState = {
    isFetching: initialState.isFetching,
    isSuccess: initialState.isSuccess,
    isError: initialState.isError,
    errorMessage: initialState.errorMessage,
    successMessage: '',
    menu_list: [],
    menu_category: initialState.venue_menu.menu_category,
    menu_category_property: false,
    active_menu_id: LocalStorage.getValue(storageType.ACTIVE_MENU_ID) || 0,
    active_category_id: 0,
    active_category: {},
    menu_subcategory_property: false,
    active_subcategory_id: 0,
    active_subcategory: {},
};
const VenueMenu = createSlice({
    name: 'venueMenu',
    initialState: venueMenuState,
    reducers: {
        clearState(state) {
            Object.assign(state, venueMenuState);
        },
        setActiveMenu(state, { payload }) {
            let menuId = payload || 0;
            state.active_menu_id = menuId;
            LocalStorage.setValue(storageType.ACTIVE_MENU_ID, menuId);
        },
        addNewCategory(state) {
            state.menu_category.push({
                id: 0,
                title: 'Click to Edit Properties',
            });
        },
        removeNewCategory(state, action) {
            const { inputIndex } = action.payload;
            state.menu_category.splice(inputIndex, 1);
        },
        setCategoryProperties(state, { payload }) {
            let { status, category } = payload;
            state.menu_category_property = status;
            state.menu_subcategory_property = false;
            state.active_category_id = category.id || 0;
            state.active_category = category;
        },
        addNewSubcategory(state, { payload }) {
            let categoryId = payload;
            state.menu_category.find(cat => {
                if (cat.id === categoryId) {
                    cat.VenueSubcategories.push({
                        id: 0,
                        lang_en: 'Click to Edit Properties',
                        lang_th: 'คลิกเพื่อแก้ไขคุณสมบัติ',
                        lang_ru: 'Нажмите, чтобы изменить свойства',
                        price: 0
                    });
                }
                return cat;
            });
        },
        removeNewSubcategory(state, action) {
            const { inputIndex } = action.payload;
            state.menu_category.splice(inputIndex, 1);
        },
        setSubcategoryProperties(state, { payload }) {
            let { status, category_id, subcategory } = payload;
            state.menu_category_property = false;
            state.menu_subcategory_property = status;
            state.active_category_id = category_id || 0;
            state.active_subcategory_id = subcategory.id || 0;
            state.active_subcategory = subcategory;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get menu by venue
            .addCase(getMenuByVenue.pending, (state) => {
                state.isFetching = true;
                state.successMessage = '';
            })
            .addCase(getMenuByVenue.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.menu_list = payload.data;
            })
            .addCase(getMenuByVenue.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload.error || payload.message;
            })
            // Save new category into DB
            .addCase(createVenueCategory.pending, (state) => {
                state.isFetching = true;
                state.successMessage = '';
            })
            .addCase(createVenueCategory.fulfilled, (state, { payload }) => {
                if (payload.action_status === 'insert') {
                    state.active_menu_id = payload.data.menu_id;
                    LocalStorage.setValue(storageType.ACTIVE_MENU_ID, payload.data.menu_id);
                }
                state.isFetching = false;
                state.isSuccess = true;
                state.successMessage = payload.message;
            })
            .addCase(createVenueCategory.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload.error || payload.message;
            })
            // Save new sub-category into DB
            .addCase(saveNewSubcategory.pending, (state) => {
                state.isFetching = true;
                state.successMessage = '';
            })
            .addCase(saveNewSubcategory.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.isSuccess = true;
                state.successMessage = payload.message;
            })
            .addCase(saveNewSubcategory.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload.error || payload.message;
            })
            // Update sub-category
            .addCase(updateSubcategory.pending, (state) => {
                state.isFetching = true;
                state.successMessage = '';
            })
            .addCase(updateSubcategory.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.isSuccess = true;
                state.successMessage = payload.message;
            })
            .addCase(updateSubcategory.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.isError = true;
                state.errorMessage = payload?.error || payload?.message || 'Something went wrong.';
            })
            // Get categories by Venue
            .addCase(getCategoryByVenue.fulfilled, (state, { payload }) => {
                state.menu_category = payload;
                state.isFetching = false;
                state.isSuccess = false;
                state.successMessage = '';
            });
    }
});

export const selectMenuCategory = (state) => state.venuemenu.menu_category;
export const selectMenuList = (state) => state.venuemenu.menu_list;
// Insert & Update = Section
export const categorySection = (params, venueId) => async (dispatch) => {
    const res = await dispatch(createVenueCategory(params));
    // Close the add/edit modal on a successful save (the list re-fetch below shows the new row).
    if (res?.meta?.requestStatus === 'fulfilled') {
        dispatch(setCategoryProperties({ status: false, category: {} }));
    }
    return dispatch(getCategoryByVenue(venueId));
}
export const subCategorySection = (params, venueId) => async (dispatch) => {
    const res = await dispatch(saveNewSubcategory(params));
    if (res?.meta?.requestStatus === 'fulfilled') {
        dispatch(setSubcategoryProperties({ status: false, category_id: 0, subcategory: {} }));
    }
    return dispatch(getCategoryByVenue(venueId));
}
export const updateAndGetSubcategory = (params) => async (dispatch) => {
    await dispatch(updateSubcategory(params));
    return dispatch(getCategoryByVenue(params.data.venue_id));
}
// Toggle a whole category visible/hidden to diners, then refresh the builder list.
export const updateCategoryActive = (params) => async (dispatch) => {
    await API.putData(`venue-category/active/${params.id}`, { is_active: params.is_active });
    return dispatch(getCategoryByVenue(params.venue_id));
}
export const updateMenuStatus = (params) => async (dispatch) => {
    await dispatch(updateMenu(params.data));
    return dispatch(getMenuByVenue(params.venue_id));
}
export const {
    clearState,
    setActiveMenu,
    addNewCategory,
    addNewSubcategory,
    removeNewCategory,
    removeNewSubcategory,
    setCategoryProperties,
    setSubcategoryProperties,
} = VenueMenu.actions;
export default VenueMenu.reducer;